"use client"
import { ark } from "@ark-ui/react/factory"
import { Select as ArkSelect, useSelectItemContext } from "@ark-ui/react/select"
import { FiChevronDown } from "react-icons/fi"
import { MdCheck } from "react-icons/md"
import { styled } from "styled-system/jsx"
import type { HTMLStyledProps } from "styled-system/types"

const { HiddenSelect } = ArkSelect

const Root = styled(
  ArkSelect.Root,
  {
    base: {
      display: "flex",
      flexDirection: "column",
      gap: "[1.5px]",
      position: "relative",
    },
  },
  {
    defaultProps: {
      positioning: {
        placement: "bottom-start",
        flip: false,
      },
    },
  },
)

const ClearTrigger = styled(ArkSelect.ClearTrigger, {
  base: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
})
const Content = styled(ArkSelect.Content, {
  base: {
    backgroundColor: "[white]",
    rounded: true,
    border: true,
    maxHeight: "[280px]",
    outline: "[0]",
    overflowY: "hidden",
    textStyle: "input",
    zIndex: "dropdown",
    transformOrigin: "var(--transform-origin)",
    _open: {
      animationDuration: "slow",
      "&[data-placement^=top]": {
        animationName: "[slide-from-bottom, fade-in]",
      },
      "&[data-placement^=bottom]": {
        animationName: "[slide-from-top, fade-in]",
      },
    },
    _closed: {
      animationDuration: "fastest",
      "&[data-placement^=top]": {
        animationName: "[slide-to-bottom, fade-out]",
      },
      "&[data-placement^=bottom]": {
        animationName: "[slide-to-top, fade-out]",
      },
    },
  },
})
const Control = styled(ArkSelect.Control, {
  base: {
    alignItems: "center",
    display: "flex",
    gap: "xs",
  },
})
const IndicatorGroup = styled(ark.div, {
  base: {
    alignItems: "center",
    display: "flex",
    gap: "xs",
  },
})
const Item = styled(ArkSelect.Item, {
  base: {
    alignItems: "center",
    cursor: "pointer",
    paddingX: "sm",
    display: "flex",
    gap: "sm",
    textAlign: "left",
    justifyContent: "space-between",
    minHeight: "sm",
    paddingRight: "md",
    paddingY: "sm",
    rounded: true,
    textStyle: "input",
    userSelect: "none",
    _hover: {
      color: "text",
      backgroundColor: "secondary",
    },
    _highlighted: {
      color: "text",
      backgroundColor: "secondary",
    },
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
  },
})
const ItemGroup = styled(ArkSelect.ItemGroup)
const ItemGroupLabel = styled(ArkSelect.ItemGroupLabel)
const ItemText = styled(ArkSelect.ItemText, {
  base: {
    paddingX: "sm",
  },
})
const Label = styled(ArkSelect.Label, {
  base: {
    userSelect: "none",
  },
})
const List = styled(ArkSelect.List, {
  base: {
    display: "grid",
    gap: "sm",
    listStyle: "none",
    margin: "[0]",
    maxHeight: "[280px]",
    overflowY: "auto",
    padding: "sm",
    outline: "[none]",
    _focusVisible: {
      outline: "[none]",
    },
    _focusWithin: {
      outline: "[none]",
    },
  },
})
const Positioner = styled(ArkSelect.Positioner)
const Trigger = styled(ArkSelect.Trigger, {
  base: {
    alignItems: "center",
    border: true,
    cursor: "pointer",
    display: "flex",
    flex: "1",
    height: "[32px]",
    justifyContent: "space-between",
    minWidth: "[0]",
    maxWidth: "[230px]",
    outline: "[0]",
    paddingX: "sm",
    textAlign: "start",
    textStyle: "input",
    transition: "common",
    userSelect: "none",
    backgroundColor: "[white]",

    rounded: true,
    _placeholderShown: {
      color: "text.muted",
    },
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
  },
})
const ValueText = styled(ArkSelect.ValueText, {
  base: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "[230px]",
  },
})
const Indicator = styled(
  ArkSelect.Indicator,
  {
    base: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: "sm",
      color: "text",
    },
  },
  {
    defaultProps: {
      children: <FiChevronDown size={16} />,
    },
  },
)

const StyledItemIndicator = styled(ArkSelect.ItemIndicator, {
  base: {
    color: "primary",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
})

const ItemIndicator = (props: HTMLStyledProps<"div">) => {
  const item = useSelectItemContext()
  const visibility = item.selected ? "visible" : "hidden"

  return (
    <StyledItemIndicator
      {...props}
      visibility={visibility}
    >
      <MdCheck size={14} />
    </StyledItemIndicator>
  )
}

export const SelectComponent = {
  Root,
  HiddenSelect,
  ClearTrigger,
  Content,
  Control,
  IndicatorGroup,
  Item,
  ItemGroup,
  ItemGroupLabel,
  ItemText,
  Label,
  List,
  Positioner,
  Trigger,
  ValueText,
  Indicator,
  ItemIndicator,
} as const
